import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";
import moment, { type Moment } from "moment-timezone";

import { type IncidentReport, type IncidentReportFollowUpContact } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from "../../hooks/useThrottledSectionUpdate";
import { FormControlWrapper } from "components/Shared";
import { TextBox, ComboBox, DateInput } from "components/Shared/Inputs";
import { confirm } from "components/Shared/ConfirmDialog";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";

import "./FollowUps.scss";

const MAX_CHARS = 2000;
const REFERRER_MAX_CHARS = 1000;
const MAX_FOLLOW_UPS = 30;
const FOLLOW_UP_NUMBER_OPTIONS = Array.from({ length: MAX_FOLLOW_UPS }, (_, i) => i + 1);

const EMPTY_CONTACT: IncidentReportFollowUpContact = {
  referralDate: null,
  referrerTitle: '',
  referrerName: '',
  contactInformation: '',
  description: '',
};

export const FollowUps: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate('followUp', values.followUp);
  const [expandedContactIndex, setExpandedContactIndex] = useState<number | false>(-1);

  const handleSessionsChange = async (wereThereAnyFollowUpSessions: boolean) => {
    if (!wereThereAnyFollowUpSessions && values.followUp?.wereThereAnyFollowUpSessions) {
      const confirmed = await confirm({
        title: t('clear_follow_up_contacts_confirm_title'),
        message: t('clear_follow_up_contacts_confirm_text'),
        confirmLabel: t('yes_remove'),
      });
      if (!confirmed) return;
    }

    const followUpContacts = wereThereAnyFollowUpSessions
      ? (values.followUp?.followUpContacts?.length ? values.followUp?.followUpContacts : [{ ...EMPTY_CONTACT }])
      : [];
    const noOfFollowUps = wereThereAnyFollowUpSessions ? (values.followUp?.noOfFollowUps || 1) : 0;

    setFieldValue('followUp.wereThereAnyFollowUpSessions', wereThereAnyFollowUpSessions);
    setFieldValue('followUp.noOfFollowUps', noOfFollowUps);
    setFieldValue('followUp.followUpContacts', followUpContacts);
    throttledUpdateReport({ wereThereAnyFollowUpSessions, noOfFollowUps, followUpContacts });
  };

  const handleNoOfFollowUpsChange = (noOfFollowUps: number) => {
    const currentContacts = values.followUp?.followUpContacts;
    const contactsToAdd = Math.max(0, noOfFollowUps - currentContacts.length);
    const followUpContacts = [...currentContacts, ...Array.from({ length: contactsToAdd }, () => ({ ...EMPTY_CONTACT }))];

    setFieldValue('followUp.noOfFollowUps', noOfFollowUps);
    setFieldValue('followUp.followUpContacts', followUpContacts);
    throttledUpdateReport({ noOfFollowUps, followUpContacts });
  };

  const handleRemoveContact = (index: number) => {
    const followUpContacts = values.followUp?.followUpContacts.filter((_, i) => i !== index);
    const noOfFollowUps = followUpContacts.length;

    setFieldValue('followUp.followUpContacts', followUpContacts);
    setFieldValue('followUp.noOfFollowUps', noOfFollowUps);
    throttledUpdateReport({ followUpContacts, noOfFollowUps });
  };

  const updateContactField = (index: number, field: keyof IncidentReportFollowUpContact, value: unknown) => {
    const followUpContacts = values.followUp?.followUpContacts.map((contact, i) => i === index ? { ...contact, [field]: value } : contact);
    throttledUpdateReport({ followUpContacts });
  };

  const makeContactChangeHandler = (index: number, field: keyof IncidentReportFollowUpContact) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(event);
      updateContactField(index, field, event.target.value);
    };

  const handleContactDateChange = (index: number, field: keyof IncidentReportFollowUpContact) => (value: Moment | null) => {
    const isoString = value?.startOf('day')?.toDate().toISOString();
    setFieldValue(`followUp.followUpContacts[${index}].${field}`, isoString);
    updateContactField(index, field, isoString);
  };

  return translationsReady && (
    <div className="follow-ups">
      <h2>{t('followUp')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesno"
            label={`${t('were_there_any_follow_up_sessions')}*`}
            name="followUp.wereThereAnyFollowUpSessions"
            fullWidth
            error={touched.followUp?.wereThereAnyFollowUpSessions && Boolean(errors?.followUp?.wereThereAnyFollowUpSessions)}
            helperText={touched.followUp?.wereThereAnyFollowUpSessions ? t(errors?.followUp?.wereThereAnyFollowUpSessions || ' ') : ''}
          >
            <ChooseYesNo
              label={`${t('were_there_any_follow_up_sessions')}*`}
              name="followUp.wereThereAnyFollowUpSessions"
              value={values.followUp?.wereThereAnyFollowUpSessions}
              onChange={(value) => handleSessionsChange(value as boolean)}
            />
          </FormControlWrapper>
        </Grid>

        {values.followUp?.wereThereAnyFollowUpSessions && (
          <>
            <Grid item sm={12}>
              <FormControlWrapper
                isReadonly={isReadonly}
                type="select"
                fullWidth
                label={`${t('number_of_follow_ups')}*`}
                name="followUp.noOfFollowUps"
                error={touched.followUp?.noOfFollowUps && Boolean(errors?.followUp?.noOfFollowUps)}
                helperText={touched.followUp?.noOfFollowUps && t(errors?.followUp?.noOfFollowUps || ' ')}
                options={FOLLOW_UP_NUMBER_OPTIONS.map(n => ({ label: `${n}`, value: `${n}` }))}
              >
                <ComboBox
                  name="followUp.noOfFollowUps"
                  options={FOLLOW_UP_NUMBER_OPTIONS}
                  getOptionLabel={(option) => `${option}`}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionDisabled={(option) =>
                    option <= values.followUp?.followUpContacts?.length &&
                    option !== values.followUp?.noOfFollowUps
                  }
                  disableClearable
                  value={values.followUp?.noOfFollowUps}
                  onChange={(_event, newValue) => newValue !== null && handleNoOfFollowUpsChange(newValue)}
                />
              </FormControlWrapper>
            </Grid>

            {values.followUp?.followUpContacts?.map((contact, index) => {
              const contactTouched = touched.followUp?.followUpContacts?.[index] as Record<string, unknown> | undefined;
              const contactErrors = errors?.followUp?.followUpContacts?.[index] as Record<string, string> | undefined;

              return (
                <Grid item sm={12} key={index}>
                  <ReportSectionAccordion
                    label={t('follow_up_contact_number', { number: index + 1 })}
                    name={`followUp.followUpContacts[${index}]`}
                    pristineValue={EMPTY_CONTACT}
                    removeAction={isReadonly ? undefined : () => handleRemoveContact(index)}
                    removeHidden={values.followUp?.followUpContacts.length <= 1}
                    expanded={expandedContactIndex === index}
                    onToggle={(isExpanded) => setExpandedContactIndex(isExpanded ? index : false)}
                    readonly={isReadonly}
                  >
                    <Grid container spacing={2}>
                      <Grid item sm={12}>
                        <FormControlWrapper
                          isReadonly={isReadonly}
                          type="date"
                          dateFormat="L"
                          label={t('referral_date')}
                          name={`followUp.followUpContacts[${index}].referralDate`}
                        >
                          <DateInput
                            label={t('referral_date')}
                            required
                            name={`followUp.followUpContacts[${index}].referralDate`}
                            onChange={handleContactDateChange(index, 'referralDate')}
                            value={contact.referralDate ? moment(contact.referralDate) : null}
                            error={Boolean(contactTouched?.referralDate) && Boolean(contactErrors?.referralDate)}
                            helperText={contactTouched?.referralDate ? t(contactErrors?.referralDate || ' ') : ''}
                            fullWidth
                          />
                        </FormControlWrapper>
                      </Grid>
                      <Grid item sm={6}>
                        <FormControlWrapper
                          isReadonly={isReadonly}
                          label={t('referrer_title')}
                          name={`followUp.followUpContacts[${index}].referrerTitle`}
                          fullWidth
                        >
                          <TextBox
                            label={t('referrer_title')}
                            required
                            name={`followUp.followUpContacts[${index}].referrerTitle`}
                            value={contact.referrerTitle}
                            onChange={makeContactChangeHandler(index, 'referrerTitle')}
                            slotProps={{ input: { maxLength: REFERRER_MAX_CHARS } }}
                            error={Boolean(contactTouched?.referrerTitle) && Boolean(contactErrors?.referrerTitle)}
                            helperText={contactTouched?.referrerTitle ? t(contactErrors?.referrerTitle || ' ') : ''}
                          />
                        </FormControlWrapper>
                      </Grid>
                      <Grid item sm={6}>
                        <FormControlWrapper
                          isReadonly={isReadonly}
                          label={t('referrer_name')}
                          name={`followUp.followUpContacts[${index}].referrerName`}
                          fullWidth
                        >
                          <TextBox
                            label={t('referrer_name')}
                            required
                            name={`followUp.followUpContacts[${index}].referrerName`}
                            value={contact.referrerName}
                            onChange={makeContactChangeHandler(index, 'referrerName')}
                            slotProps={{ input: { maxLength: REFERRER_MAX_CHARS } }}
                            error={Boolean(contactTouched?.referrerName) && Boolean(contactErrors?.referrerName)}
                            helperText={contactTouched?.referrerName ? t(contactErrors?.referrerName || ' ') : ''}
                          />
                        </FormControlWrapper>
                      </Grid>
                      <Grid item sm={12}>
                        <FormControlWrapper
                          isReadonly={isReadonly}
                          label={t('contact_information')}
                          name={`followUp.followUpContacts[${index}].contactInformation`}
                          fullWidth
                        >
                          <TextBox
                            multiline
                            minRows={4}
                            maxRows={6}
                            label={t('contact_information')}
                            required
                            name={`followUp.followUpContacts[${index}].contactInformation`}
                            value={contact.contactInformation}
                            onChange={makeContactChangeHandler(index, 'contactInformation')}
                            slotProps={{ input: { maxLength: MAX_CHARS } }}
                            error={Boolean(contactTouched?.contactInformation) && Boolean(contactErrors?.contactInformation)}
                            helperText={contactTouched?.contactInformation ? t(contactErrors?.contactInformation || ' ') : ''}
                          />
                        </FormControlWrapper>
                      </Grid>
                      <Grid item sm={12}>
                        <FormControlWrapper
                          isReadonly={isReadonly}
                          label={t('description')}
                          name={`followUp.followUpContacts[${index}].description`}
                          fullWidth
                        >
                          <TextBox
                            multiline
                            minRows={4}
                            maxRows={6}
                            label={t('description')}
                            required
                            name={`followUp.followUpContacts[${index}].description`}
                            value={contact.description}
                            onChange={makeContactChangeHandler(index, 'description')}
                            slotProps={{ input: { maxLength: MAX_CHARS } }}
                            error={Boolean(contactTouched?.description) && Boolean(contactErrors?.description)}
                            helperText={contactTouched?.description ? t(contactErrors?.description || ' ') : ''}
                          />
                        </FormControlWrapper>
                      </Grid>
                    </Grid>
                  </ReportSectionAccordion>
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
    </div>
  );
};
